import { usePageView } from '@/analytics/pageTypes';
import { Layout } from '@/components/layout';
import { SeoHeader } from '@/components/SeoHeader';
import { DocumentSection } from '@/sections/documents/DocumentSection';
import { useTranslation } from 'react-i18next';
import CardFront from '@/assets/icons/heroIcons/Card-front.svg';
import { Button } from '@/components/buttons';
import type { GetStaticProps } from 'next';
import { getIntlProps, type IntlProps } from '@/utils/intl';
import { useState } from 'react';
import { useRouter } from 'next/router';
import { useCustomerRequestCreateQuery } from '@/api/skippay/customerRequestCreateQuery';
import Loader from '@/components/Loader';
import { useIsLoggedIn } from '@/hooks/useIsLoggedIn';
import { RefundSentLoggedIn } from '@/sections/overpayRefund/sent/RefundSentLoggedIn';
import { RefundSentLogout } from '@/sections/overpayRefund/sent/RefundSentLogout';
import { RefundToAccountForm } from '@/sections/overpayRefund/form/RefundToAccountForm';
import { FIELD_BANK_CODE, FIELD_FIRST_PART, FIELD_SECOND_PART, FIELD_THIRD_PART } from '@mallpay/orbis';
interface Props extends IntlProps {}
export default function Page() {
  const {
    t
  } = useTranslation(['overpayRefund', 'core']);
  const router = useRouter();
  const {
    productType: productTypeFromQuery
  } = router.query;
  usePageView('overpay-refund');
  const [refundSent, setRefundSent] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const customerRequestQuery = useCustomerRequestCreateQuery();
  const {
    isLoggedIn,
    email: loggedInEmail
  } = useIsLoggedIn();
  const productType = (productTypeFromQuery as string)?.toUpperCase() === 'INSTALLMENT' ? 'INSTALLMENT' : (productTypeFromQuery as string)?.toUpperCase() === 'REVOLVING' ? 'REVOLVING' : 'BNPL';
  function renderContent() {
    if (customerRequestQuery.isPending) {
      return <Loader hideLabel />;
    }
    if (submitError) {
      return <div className="mb-[160px] flex flex-col items-center justify-center gap-6 rounded-4xl bg-white p-10 lg:p-12">
					<p className="mb-6 text-sm font-semibold text-red-500 lg:text-base">
						{t('refundToAccount.error')}
					</p>
					<Button variant="unstyled" onClick={() => setSubmitError(false)} analyticsParam="tryAgain" testId="tryAgain" className="text-blue-500 underline decoration-1 hover:text-blue-800" align="center">
						{t('refundToAccount.tryAgain')}
					</Button>
				</div>;
    }
    if (refundSent) {
      if (isLoggedIn) {
        return <RefundSentLoggedIn />;
      }
      if (!isLoggedIn) {
        return <RefundSentLogout />;
      }
    }
    async function handleSubmitAccount(values: {
      email?: string;
      bankAccount: {
        [FIELD_BANK_CODE]: string;
        [FIELD_FIRST_PART]?: string;
        [FIELD_SECOND_PART]: string;
        [FIELD_THIRD_PART]?: string;
      };
    }) {
      try {
        const accountNumber = [values.bankAccount[FIELD_FIRST_PART], values.bankAccount[FIELD_SECOND_PART], values.bankAccount[FIELD_THIRD_PART]].filter(Boolean).join('-');
        const bankAccountNumber = `${accountNumber}/${values.bankAccount[FIELD_BANK_CODE]}`;
        await customerRequestQuery.mutateAsync({
          email: (isLoggedIn ? loggedInEmail : values.email) ?? '',
          requestType: 'REFUND',
          subject: 'Chci požádat o vrácení přeplatku',
          extraData: {
            productType,
            accountNumber: bankAccountNumber,
            refundType: 'BANK_ACCOUNT'
          }
        });
        setRefundSent(true);
      } catch (error) {
        setSubmitError(true);
      }
    }
    return <div className="mb-38 flex flex-col gap-6 lg:w-192">
				{isLoggedIn ? <DocumentSection title={t('refundToAccount.title')} icon={<div className="flex items-center justify-center lg:h-10 lg:w-10">
								<CardFront className="text-blue-500" />
							</div>} defaultOpen={false} dropdownClassName="overflow-initial">
						<RefundToAccountForm handleSubmit={handleSubmitAccount} disabled={customerRequestQuery.isPending} isLoggedIn={isLoggedIn} />
					</DocumentSection> : <div className="rounded-2xl bg-white px-4 py-6 lg:p-8">
						<RefundToAccountForm handleSubmit={handleSubmitAccount} disabled={customerRequestQuery.isPending} isLoggedIn={isLoggedIn} />
					</div>}
			</div>;
  }
  function getDescription(): string {
    if (isLoggedIn) {
      return t('description.loggedInWithOnlyAccount');
    }
    return t('description.loggedOut');
  }
  return <Layout>
			<SeoHeader title={t('seo.title')} description={t('seo.description')} nofollow noindex />

			<div className="relative z-30 bg-blue-100 px-4">
				<div className="bgCircle absolute top-1/4 left-0 hidden -translate-x-1/4 -translate-y-1/2 rotate-90 lg:block" />
				<div className="relative z-10 mx-auto max-w-[60.5rem]">
					<div className="flex min-h-[60vh] flex-col items-center justify-center gap-20">
						<div className="mt-38 flex flex-col items-center gap-8">
							<h1 className="mb-4 text-3xl font-bold lg:text-[64px]">{t('title')}</h1>
							{!refundSent && <p className="text-center text-lg lg:text-[20px]">{getDescription()}</p>}
						</div>
						{renderContent()}
					</div>
				</div>
			</div>
		</Layout>;
}
export const getStaticProps = (async ctx => {
  return {
    props: {
      ...(await getIntlProps(ctx, ['overpayRefund', 'core']))
    }
  };
}) satisfies GetStaticProps<Props>;